/* eslint-disable */
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { PersistGate } from 'redux-persist/integration/react';
import { USER_SERVICE_PRESENCE_STATUS_PRESENT } from '../../Constants/User/User.constants';
import withEntries from '../../Contexts/Entries/WithEntries.context';
import withServices from '../../Contexts/Services/WithServices.context';
import authActionCreators from '../../State/Store/Auth/Auth.actions';
import authPresenceSelector from '../../State/Store/Auth/Selectors/AuthCurrentPresence.selector';
import authExpirySelector from '../../State/Store/Auth/Selectors/AuthExpiry.selector';
import i18nLocaleSelector from '../../State/Store/I18n/Selectors/I18nLocale.selector';
import authCurrentSelector from '../../State/Store/Users/Selectors/UserCurrent.selector';
import './Loading.component.css';
import './Loading.component.i18n';
import LoadingPropTypes from './Loading.component.props';

/**
 * LoadingComponent
 * @export
 * @class LoadingComponent
 * @extends {React.Component<LoadingComponentComposedProps, LoadingComponentState>}
 */
export class LoadingComponent extends React.Component {
  /**
   * The `props` to seed when any specific prop receives an `undefined` value. **NOT** a `null` value
   * @static
   * @type {Partial<LoadingComponentProps>}
   * @memberof LoadingComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`Loading`}
   * @memberof LoadingComponent
   */
  static displayName = `Loading`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof LoadingComponent
   */
  static propTypes = LoadingPropTypes;

  /**
   * Data specific to the `<LoadingComponent />` instance
   * @type {LoadingComponentState}
   * @memberof LoadingComponent
   */
  state = {
    authPresenceLoading: true,
    authStateLoading: true,
    persistLoading: true,
  };

  /**
   * A method to be called when unmounting to remove the listener to the auth presence changes
   * @type {AuthServicePresenceChangedUnsubscribe}
   * @memberof LoadingComponent
   */
  unsubscribeAuthPresence;

  /**
   * A method to be called when unmounting to remove the listener to the auth state changes
   * @type {AuthServiceStateChangedUnsubscribe}
   * @memberof LoadingComponent
   */
  unsubscribeAuthState;

  /**
   * If `this.props.auth` ever changes, either logout or refresh
   * @param {React.props} prevProps The `props` prior to the update
   * @returns {void} void
   * @memberof LoadingComponent
   */
  componentDidUpdate(prevProps) {
    const { auth: prevAuth } = prevProps;
    const { services } = this.props;
    if (prevAuth === null && !services.authService.isAuthenticated()) {
      return;
    }
    if (this.isSessionLoggedOut(prevAuth)) {
      this.handleSessionLoggedOut();
      return;
    }
    const { authPresence } = prevProps;
    if (prevAuth !== null && this.isPresenceChanged(authPresence) && this.isUserPresent()) {
      this.refreshSession();
    }
  }

  /**
   * When the Component mounts, check to make sure the required entities are loaded
   * @returns {void} void
   * @memberof LoadingComponent
   */
  componentDidMount() {
    const { actions } = this.props;
    this.unsubscribeAuthState = actions.authOnAuthStateChanged(this.handleAuthStateChanged);
    this.unsubscribeAuthPresence = actions.authOnAuthPresenceChanged(this.handleAuthPresenceChanged);
  }

  /**
   * Remove the entity listeners
   * @returns {void} void
   * @memberof LoadingComponent
   */
  componentWillUnmount() {
    this.unsubscribeAuthState();
    this.unsubscribeAuthPresence();
  }

  /**
   * Checks if the user's presence has changed.
   * @param {AuthServicePresenceStatus} prevAuthPresence The previous auth presence.
   * @returns {boolean} Returns true if the user presence has changed, otherwise, false.
   * @memberof LoadingComponent
   */
  isPresenceChanged = (prevAuthPresence) => {
    const { authPresence } = this.props;
    return authPresence !== prevAuthPresence;
  };

  /**
   * Determine if the content has finished loading
   * @returns {boolean} Whether or not we are still loading
   * @memberof LoadingComponent
   */
  isLoading = () => {
    const { authPresenceLoading, authStateLoading, persistLoading } = this.state;
    // If any of these properties are `true`, then we are still loading
    return authPresenceLoading || authStateLoading || persistLoading;
  };

  /**
   * Checks if the user is logged out from a session
   * @param {AuthEntity} prevAuth The previous auth entity.
   * @returns {boolean} Returns true if the user is on a new session, otherwise, false.
   * @memberof LoadingComponent
   */
  isSessionLoggedOut = (prevAuth) => {
    const { services } = this.props;
    return prevAuth && !services.authService.isAuthenticated();
  };

  /**
   * Checks if the current user is "present"
   * @returns {boolean} Returns true if the user is "present", otherwise, false.
   * @memberof LoadingComponent
   */
  isUserPresent = () => {
    const { authPresence } = this.props;
    return authPresence === USER_SERVICE_PRESENCE_STATUS_PRESENT;
  };

  /**
   * Handle the callback for `onAuthPresenceChanged`
   * @returns {void} void
   * @memberof LoadingComponent
   */
  handleAuthPresenceChanged = () => {
    const { authPresenceLoading } = this.state;
    if (!authPresenceLoading) {
      return;
    }
    this.setState({
      authPresenceLoading: false,
    });
  };

  /**
   * Handle the callback for `onAuthStateChanged`
   * @returns {void} void
   * @memberof LoadingComponent
   */
  handleAuthStateChanged = () => {
    const { authStateLoading } = this.state;
    if (!authStateLoading) {
      return;
    }
    this.setState({
      authStateLoading: false,
    });
  };

  /**
   * Handle logged out
   * @returns {void} void
   * @memberof LoadingComponent
   */
  handleSessionLoggedOut = () => {
    const { entry, history } = this.props;
    history.replace(entry.postLogoutLink);
  };

  /**
   * When `redux-persist` goes through it's _ready_ lifecycle, it will execute this method. When this methods is called with `true`, `redux-persist` is ready
   * @param {boolean} persistReady The `ready` state of `redux-persist`
   * @returns {null} Do not render content
   * @memberof LoadingComponent
   */
  handlePersistReady = (persistReady) => {
    if (!persistReady) {
      return null;
    }
    const { lang, services } = this.props;
    // Set the language to whatever was pulled from storage
    services.i18nService.setLocale({ lang });
    // Because this method is called from within the body of `<PersistGate />`, it will trigger a `setState` from within a `render`, it's for that reason that
    // we need to wrap the following code in a `setTimeout`. It's not ideal, but due to the design of `<PersistGate />` it's unavoidable
    setTimeout(() => {
      this.setState({
        persistLoading: false,
      });
    }, 1);
    return null;
  };

  /**
   * Refresh the current user session
   * @returns {void} void
   * @memberof LoadingComponent
   */
  refreshSession = () => {
    const { services } = this.props;
    services.authService.refreshAccessToken();
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<LoadingComponent />`
   * @memberof LoadingComponent
   */
  render() {
    const { children } = this.props;
    if (this.isLoading()) {
      const { persistor } = this.props;
      return <PersistGate persistor={persistor}>{this.handlePersistReady}</PersistGate>;
    }
    return children;
  }
}

/** @type {React.ComponentClass<LoadingComponentProps>} */
const Loading = compose(
  withEntries,
  withServices,
  connect(
    (state) => ({
      auth: authCurrentSelector(state),
      authExpiry: authExpirySelector(state),
      authPresence: authPresenceSelector(state),
      lang: i18nLocaleSelector(state).lang,
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...authActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(LoadingComponent);

export default Loading;
